import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Avatar
} from "@mui/material";
import Cropper from "react-easy-crop";
import CloseIcon from "@mui/icons-material/Close";
import { Edit } from "@mui/icons-material";
import { getCroppedImg } from "../../../utils/ImageCrop";
import { toast } from "react-toastify";
import apiService from "../../../service/Api";

interface EditAvatarProps {
  initialImage: string | undefined;
  pilot: {
    id: number;
    name: string;
  };
};

export const EditAvatar: React.FC<EditAvatarProps> = ({
  initialImage,
  pilot
}) => {
  const [image, setImage] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<string | undefined>(initialImage);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [open, setOpen] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(false);

  const [avatarHovered, setAvatarHovered] = useState<boolean>(false);

  useEffect(() => {
    setPreviewImage(initialImage);
  }, [initialImage]);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result as string);
        setOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCropComplete = (_: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleCropSave = async () => {
    if (!image || !croppedAreaPixels) return;
    const croppedImage = await getCroppedImg(image, croppedAreaPixels);
    setPreviewImage(croppedImage);
    handleAvatarUpdate(croppedImage);
    setOpen(false);
  };

  const handleAvatarUpdate = async (image: string) => {
    try {
      const updating = await apiService.patch(`/api/authentication/users/${pilot.id}/`, {
        profile_picture: image
      });

      console.log({ updating });

      toast.success(`You have successfully updated ${pilot.name}'s avatar!`);
    } catch (err) {
      console.log(err, 'Pilot Avatar Updating Error');
      toast.error(`Failed to update ${pilot.name}'s avatar!`);
    }
  };

  return (
    <Box>
      <Box
        onMouseEnter={() => setAvatarHovered(true)}
        sx={{
          position: 'relative',
          width: 80,
          height: 80,
          borderRadius: '50%',
          overflow: 'hidden',
          cursor: 'pointer',
          '&:hover .overlay': {
            opacity: 1
          }
        }}
        onMouseLeave={() => setAvatarHovered(false)}
      >
        <Avatar
          src={initialImage}
          sx={{
            width: 80,
            height: 80,
            '&:hover': {

            },
            borderRadius: "50%"
          }}
        />
        <Box
          className={'overlay'}
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: '#00000070',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            opacity: 0,
            transition: 'opacity 0.3s ease-in-out'
          }}
        >
          <label htmlFor="upload-avatar">
            <IconButton
              color="primary"
              sx={{ color: 'white' }}
              component="span"
            >
              <Edit />
            </IconButton>
          </label>
          <input
            accept="image/png,image/jpeg,image/jpg"
            id={'upload-avatar'}
            style={{ display: 'none' }}
            type="file"
            onChange={handleAvatarChange}
          />
        </Box>
      </Box>      

      {/* Cropping Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {`Updating ${pilot.name} Avatar`}
          <IconButton
            onClick={() => setOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ position: "relative", height: 400 }}>
          {image && (
            <Cropper
              image={image}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={handleCropComplete}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button sx={{ color: "#724E41" }} onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCropSave}
            variant="contained"
            sx={{ backgroundColor: "#724E41" }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={previewDialog}
        onClose={() => setPreviewDialog(false)}
        maxWidth="sm"
      >
        <DialogTitle>
          Image Preview
          <IconButton
            onClick={() => setPreviewDialog(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center" }}>
          {previewImage && (
            <Box
              component="img"
              src={previewImage}
              alt="Full Preview"
              sx={{ maxWidth: "100%" }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
